// #/app/[workspace]/page.tsx

'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { ReactFlowProvider } from 'reactflow';
import { toast } from 'react-toastify';
import Image from 'next/image';

import { FlowProvider, useFlowContext } from '#/providers/FlowContext';
import { User } from '#/services/user';

import AuthModal from '~/Flow/AuthModal';
import Flow from '~/Flow/Flow';
import NodeDetailsModal from '~/Flow/NodeDetailsModal';
import WorkspaceSettingsModal from '~/Flow/SettingsModal';
import { AddressBar } from '~/Layout/AddressBar';
import LoadingIndicator from '~/ui/LoadingIndicator';

function WorkspaceView({ workspaceUuid }) {
  const {
    isLoading,
    workspace,
    isSettingsShown,
    isNodeDetailsShown,
    isAuthModalShown,
  } = useFlowContext();

  const [isShiftPressed, setIsShiftPressed] = useState(false);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    setIsShiftPressed(event.shiftKey);
  }

  function handleKeyUp(event: React.KeyboardEvent<HTMLDivElement>) {
    setIsShiftPressed(event.shiftKey);
  }

  function handleSelect(event: React.KeyboardEvent<HTMLDivElement>) {
    if (isShiftPressed) {
      event.preventDefault(); // Prevents text selection
    }
  }

  const [workspaceContext, setWorkspaceContext] = useState({});
  useEffect(() => {
    setWorkspaceContext(workspace?.context ?? {});
  }, [JSON.stringify(workspace?.context)]);
  async function updateWorkspaceContext({ context }) {
    if (workspaceUuid == null || workspaceUuid === '') {
      return;
    }

    const user = new User({ address: userAddress });

    await user.updateWorkspace({
      workspaceUuid,
      updatedWorkspace: { context },
    });

    toast.success('Workspace context updated successfully!');
  }

  if (isLoading) {
    return (
      <div className="bg-transparent h-screen w-screen flex">
        <LoadingIndicator />
        <div className="visible md:invisible w-full h-full fixed z-50 grid items-center justify-center pointer-events-auto bg-primary">
          <div className="rounded-lg bg-base-100 p-8 flex flex-col items-center gap-4 relative pointer-events-auto w-80">
            <div className="relative h-[80px] w-[80px]">
              <Image
                src="/linkit.svg"
                alt="linkit logo"
                height={80}
                width={80}
              />
            </div>
            <div className="text-xs max-w-sm flex flex-col gap-2">
              <p>
                Mobile support is not implemented. Please use the app on a
                larger device. Sorry!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (workspace == null) {
    return (
      <div className="bg-transparent h-screen w-screen">
        <main className="w-full h-full relative">
          <div className="w-full h-full fixed z-0">
            <p className="text-primary">Error</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onSelect={handleSelect}
      className="bg-transparent h-screen w-screen"
    >
      <main className="w-full h-full relative">
        <div className="z-10 fixed w-full invisible md:visible">
          <div className="mx-auto max-w-4xl space-y-9 px-2 pt-20 lg:py-9 lg:px-9">
            <div className="rounded-lg bg-vc-border-gradient p-px shadow-lg shadow-black/20">
              <div className="rounded-lg bg-black">
                <AddressBar isInWorkspace={true} />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full h-full fixed z-0">
          {workspace != null && workspaceUuid != null ? (
            <Flow workspaceUuid={workspaceUuid} workspace={workspace} />
          ) : null}
        </div>
        {isSettingsShown ? (
          <WorkspaceSettingsModal
            workspaceContext={workspaceContext}
            updateWorkspaceContext={updateWorkspaceContext}
          />
        ) : isNodeDetailsShown ? (
          <NodeDetailsModal />
        ) : isAuthModalShown ? (
          <AuthModal />
        ) : null}
        <div className="visible md:invisible fixed grid items-center justify-center h-full w-full z-[9999] pointer-events-auto bg-primary">
          <div className="rounded-lg bg-base-100 p-8 flex flex-col items-center gap-4 relative pointer-events-auto w-80">
            <div className="relative h-[80px] w-[80px]">
              <Image
                src="/linkit.svg"
                alt="linkit logo"
                height={80}
                width={80}
              />
            </div>
            <div className="text-xs max-w-sm flex flex-col gap-2">
              <p>
                Mobile support is not implemented. Please use the app on a
                larger device. Sorry!
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Page() {
  const pn = usePathname();
  const np = pn?.split('/').slice(1);
  const slug = np?.toString() || '';
  const workspaceUuid = slug;

  return (
    <ReactFlowProvider>
      <FlowProvider>
        <WorkspaceView
          {...{
            workspaceUuid,
          }}
        />
      </FlowProvider>
    </ReactFlowProvider>
  );
}
